/**
 * authenticatedFetch — centralized fetch wrapper with auth header injection.
 *
 * Handles:
 * - Bearer token injection from configureDbClient() token provider
 * - Automatic 401 retry with JWT refresh
 * - Sentry trace + PostHog telemetry headers
 * - Accept-Version: v2 header
 * - Auth error callback for unrecoverable 401s
 *
 * Phase 3c (NocoDB #3920): X-Device-Id header path removed. Anonymous users
 * now carry a BA anonymous JWT (Bearer token). The getDeviceId callback on
 * ApiClientConfig is removed; no X-Device-Id header is ever injected.
 *
 * Returns the raw Response — callers handle body parsing.
 *
 * @module utils/authenticated-fetch
 */

import { getClientConfig } from '../client';
import { getSentryTraceHeaders } from '../proxy/edge-url';
import {
  getAccessTokenFn,
  getRefreshAccessTokenFn,
  getAuthStatus,
  awaitAuthReady,
  awaitAuthBootstrap,
} from '../proxy/auth-token';

/**
 * Cap on how long to wait for AuthContext bootstrap (first setAuthStatus call)
 * before falling through. Cold-load bootstraps typically complete in <100ms; 3s
 * gives ~30x headroom while keeping the worst-case UI delay perceptible-but-
 * tolerable if AuthContext is genuinely broken. Tune via NocoDB #4117 telemetry.
 */
const BOOTSTRAP_TIMEOUT_MS = 3000;

/** Callback invoked on unrecoverable 401 (refresh failed or unavailable). */
let _onAuthError: (() => void) | null = null;

/**
 * Thrown by authenticatedFetch when auth status is 'auth_broken' (BA session
 * present but JWT mint failed after retries). Surfaces the broken-auth state
 * loudly instead of silently downgrading the request to anonymous —
 * prevents the silent-fallback bug (NocoDB #3829, Sentry DRAMA-WEB-3B).
 */
export class AuthBrokenError extends Error {
  readonly code = 'AUTH_BROKEN';
  readonly status = 401;
  constructor(message = 'Auth state is broken — JWT could not be obtained for this session') {
    super(message);
    this.name = 'AuthBrokenError';
  }
}

/**
 * Register a callback for unrecoverable auth errors (e.g. session expired).
 * The UI layer (AuthErrorBoundary) uses this to show a sign-out prompt.
 */
export function setOnAuthError(callback: (() => void) | null): void {
  _onAuthError = callback;
}

export interface AuthenticatedFetchOptions extends RequestInit {
  /** Skip auth header injection (e.g. for public shared-link endpoints). */
  skipAuth?: boolean;
}

/**
 * Fetch with automatic auth header injection and 401 retry.
 *
 * @param url - Request URL
 * @param options - Standard RequestInit plus skipAuth flag
 * @returns Raw Response object
 */
export async function authenticatedFetch(
  url: string,
  options?: AuthenticatedFetchOptions,
): Promise<Response> {
  const config = getClientConfig();
  const skipAuth = options?.skipAuth ?? false;
  const shouldNotifyAuthError = (): boolean => getAuthStatus() === 'authenticated';

  const buildHeaders = async (token: string | null): Promise<Record<string, string>> => {
    const headers: Record<string, string> = {
      'Accept-Version': 'v2',
      ...getSentryTraceHeaders(),
    };

    // E2E Test Mode: add X-E2E-Test on every authenticated request when
    // EXPO_PUBLIC_E2E_TEST_MODE=true. The worker only honors it when
    // E2E_QUOTA_BYPASS_ENABLED=true is also set (staging-only) — see
    // workers/src/_shared/lib/e2e-test-mode.ts. Centralizing it here covers
    // the polar-checkout path (and any other handler with an E2E bypass)
    // without a per-call header in each api/* module or app.
    if (process.env.EXPO_PUBLIC_E2E_TEST_MODE === 'true') {
      headers['x-e2e-test'] = '1';
    }

    // PostHog telemetry headers for session replay correlation
    if (config?.getTelemetryHeaders) {
      try {
        Object.assign(headers, config.getTelemetryHeaders());
      } catch { /* best-effort */ }
    }

    if (skipAuth) return headers;

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Phase 3c (NocoDB #3920): X-Device-Id fallback removed.
    // Anonymous users now carry a BA anonymous JWT. No device header is sent.

    return headers;
  };

  // Pre-gate (NocoDB #4100): cold-load fetches can fire before AuthContext
  // mounts, leaving status at default 'anonymous' and shipping no Bearer.
  // Wait briefly for first setAuthStatus call, then for any in-flight JWT mint.
  // BOOTSTRAP_TIMEOUT_MS cap prevents hangs if AuthContext never mounts
  // (falls through to 401, same as today's behavior).
  if (!skipAuth && getAuthStatus() === 'anonymous') {
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      awaitAuthBootstrap().finally(() => clearTimeout(timer)),
      new Promise<void>((resolve) => { timer = setTimeout(resolve, BOOTSTRAP_TIMEOUT_MS); }),
    ]);
  }
  if (!skipAuth && getAuthStatus() === 'pending') {
    await awaitAuthReady();
  }

  // auth_broken short-circuit: BA session is present but the JWT mint flow has
  // failed after retries. Refuse to send the request — the alternative (sending
  // with no Bearer) makes the worker treat the request as anonymous-by-IP and
  // silently serves degraded data. Surface to the UI via _onAuthError so the
  // user can re-authenticate. NocoDB #3829 / Sentry DRAMA-WEB-3B.
  if (!skipAuth && getAuthStatus() === 'auth_broken') {
    _onAuthError?.();
    throw new AuthBrokenError();
  }

  // Read the token getter AFTER the pre-gate. Reading at call-time captured a
  // stale null during the bootstrap window (NocoDB #4100): AuthContext calls
  // configureAuthToken() concurrently with setAuthStatus() during mount, so
  // the getter is only guaranteed non-null after the bootstrap signal fires.
  const _getAccessToken = getAccessTokenFn();
  const _refreshAccessToken = getRefreshAccessTokenFn();
  let token = _getAccessToken?.() ?? null;

  // If a refresh path exists but the current getter still returns null, try the
  // shared refresh/bootstrap seam BEFORE issuing the first strict request.
  // This avoids a bearerless first call when readiness is still achievable.
  if (!skipAuth && !token && _refreshAccessToken) {
    try {
      token = await _refreshAccessToken();
    } catch {
      // Fall through — 401 handling below will classify/notify terminal cases.
    }
  }

  const { skipAuth: _, ...fetchOptions } = options ?? {};
  const headers = await buildHeaders(token);

  // Merge caller-provided headers with auth headers (caller wins on conflict)
  const mergedHeaders = {
    ...headers,
    ...(fetchOptions.headers as Record<string, string> | undefined),
  };

  const response = await fetch(url, {
    ...fetchOptions,
    headers: mergedHeaders,
  });

  // 401 retry: attempt JWT refresh and retry once.
  // Note: no `&& token` guard — requests that launched without a token (race condition)
  // must still be able to self-heal via the refresh callback.
  if (response.status === 401 && !skipAuth) {
    if (_refreshAccessToken) {
      try {
        const newToken = await _refreshAccessToken();
        if (newToken) {
          const retryHeaders = await buildHeaders(newToken);
          const retryMergedHeaders = {
            ...retryHeaders,
            ...(fetchOptions.headers as Record<string, string> | undefined),
          };
          const retryResponse = await fetch(url, { ...fetchOptions, headers: retryMergedHeaders });
          if (retryResponse.status === 401 && shouldNotifyAuthError()) {
            _onAuthError?.();
          }
          return retryResponse;
        }
      } catch {
        // Refresh failed — fall through to auth error
      }
    }

    // Unrecoverable 401: notify UI layer for real authenticated sessions even
    // when no refresh callback is available.
    if (shouldNotifyAuthError()) {
      _onAuthError?.();
    }
  }

  return response;
}
